import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { getERPClient } from "@/lib/erp/client";
import type {} from "@/lib/erp";
import { toast } from "sonner";

const SORT_PACK_API = "zaviago_backend.fulfillment.sort_pack_api";

export function useOrdersList(filters?: Record<string, any>) {
  return useQuery({
    queryKey: ["orders", "list", filters],
    queryFn: () => erp.orders.list(filters),
    placeholderData: (prev) => prev,
  });
}

export function useOrder(name: string) {
  return useQuery({
    queryKey: ["orders", "detail", name],
    queryFn: () => erp.orders.detail(name),
    enabled: !!name,
  });
}

/**
 * Hook to export personalization CSV. Triggers browser download.
 */
export function useExportPersonalizationCSV() {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async (
    salesOrder?: string,
    salesOrders?: string[],
    dateFrom?: string,
    dateTo?: string,
    sourceChannels?: string[],
    paymentStatus?: string[],
    storefrontSources?: string[],
  ) => {
    setIsExporting(true);
    try {
      const result = await erp.orders.exportPersonalizationCSV(
        salesOrder, salesOrders, dateFrom, dateTo, sourceChannels, paymentStatus, storefrontSources,
      );
      // Trigger browser download
      const blob = new Blob(["\uFEFF" + result.content], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Personalization data exported");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Export failed";
      toast.error(message);
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportCSV, isExporting };
}

// ---------------------------------------------------------------------------
// Order Tracking Hooks (non-marketplace orders)
// ---------------------------------------------------------------------------

export interface OrderTrackingInfo {
  delivery_note: string | null;
  tracking_no: string | null;
  transporter_name: string | null;
}

export function useOrderTracking(orderName: string) {
  return useQuery({
    queryKey: ["orders", "tracking", orderName],
    queryFn: () => {
      const client = getERPClient();
      return client.call<OrderTrackingInfo>(
        `${SORT_PACK_API}.get_order_tracking`,
        { sales_order: orderName },
      );
    },
    enabled: !!orderName,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useSetOrderTracking(orderName: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean },
    Error,
    { deliveryNote: string; trackingNo: string; transporterName?: string }
  >({
    mutationFn: ({ deliveryNote, trackingNo, transporterName }) => {
      const client = getERPClient();
      return client.call<{ success: boolean }>(
        `${SORT_PACK_API}.set_tracking_number`,
        {
          delivery_note: deliveryNote,
          tracking_no: trackingNo,
          ...(transporterName && { transporter_name: transporterName }),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", "tracking", orderName] });
      queryClient.invalidateQueries({ queryKey: ["orders", "detail", orderName] });
      toast.success("Tracking number saved");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save tracking number");
    },
  });
}

// ---------------------------------------------------------------------------
// Shopee Shipping Hooks (Plan 189, Epic 02)
//
// These hooks use getERPClient() directly (not erp.orders.*) because
// callBlob() for PDF download is on the ERPClient interface.
// ---------------------------------------------------------------------------

export interface ShippingOptions {
  info_needed: "pickup" | "dropoff" | "non_integrated";
  pickup?: {
    address_list: Array<{
      address_id: number;
      address: string;
      city: string;
      state: string;
    }>;
    time_slot_list: Array<{
      pickup_time_id: string;
      date: number;
      time_text: string;
    }>;
  };
  dropoff?: {
    branch_list: Array<{
      branch_id: number;
      branch_name: string;
      address: string;
    }>;
  };
  message?: string;
}

export interface ShipLabelParams {
  shippingMethod: "pickup" | "dropoff" | "non_integrated";
  methodParams: Record<string, any>;
  labelFormat: string;
}

/**
 * Fetch available Shopee shipping methods for an order.
 */
export function useShippingParameters(orderName: string) {
  return useQuery({
    queryKey: ["shopee-shipping-params", orderName],
    queryFn: () => {
      const client = getERPClient();
      return client.call<ShippingOptions>(
        "zaviago_backend.api.shopee_shipping.get_shipping_options",
        { order_name: orderName },
      );
    },
    enabled: !!orderName,
    staleTime: 5 * 60 * 1000, // 5 min — options don't change often
  });
}

/**
 * Ship a Shopee order and generate a shipping label PDF.
 * Returns a Blob (PDF binary).
 */
export function useShipAndGenerateLabel(orderName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: ShipLabelParams) => {
      const client = getERPClient();
      return client.callBlob(
        "zaviago_backend.api.shopee_shipping.ship_and_generate_label",
        {
          order_name: orderName,
          shipping_method: params.shippingMethod,
          params: JSON.stringify(params.methodParams),
          label_format: params.labelFormat,
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", "detail", orderName] });
      queryClient.invalidateQueries({ queryKey: ["orders", "list"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to ship order");
    },
  });
}

/**
 * Reprint a shipping label for an already-shipped Shopee order.
 * Returns a Blob (PDF binary). Invalidates order detail cache on success
 * so shopee_label_doc_type updates in the UI.
 */
export function useReprintLabel(orderName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (labelFormat: string) => {
      const client = getERPClient();
      return client.callBlob(
        "zaviago_backend.api.shopee_shipping.reprint_label",
        { order_name: orderName, label_format: labelFormat },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", "detail", orderName] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reprint label");
    },
  });
}

// ---------------------------------------------------------------------------
// Batch Shipping Hooks (Plan 189, Epic 03)
// ---------------------------------------------------------------------------

export interface BatchShipOrderResult {
  order_name: string;
  status: "success" | "failed" | "skipped";
  tracking_no?: string;
  error?: string;
  reason?: string;
}

export interface BatchShipResult {
  results: BatchShipOrderResult[];
  pdf_base64: string;
  success_count: number;
  failed_count: number;
  skipped_count: number;
}

/**
 * Batch ship multiple Shopee orders and get a combined label PDF.
 * Returns JSON with per-order results + base64-encoded merged PDF.
 */
export function useBatchShipOrders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      orderNames: string[];
      labelFormat: string;
    }) => {
      const client = getERPClient();
      return client.call<BatchShipResult>(
        "zaviago_backend.api.shopee_shipping.batch_ship_and_generate_labels",
        {
          order_names: JSON.stringify(params.orderNames),
          label_format: params.labelFormat,
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", "list"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Batch shipping failed");
    },
  });
}
